const mapboxApi = process.env.VUE_APP_MAPBOX_API_URL;
const resources = {
    mapboxCdn: `${mapboxApi}/${process.env.VUE_APP_MAPBOX_CDN_PATH}`,
    mapboxCss: `${mapboxApi}/${process.env.VUE_APP_MAPBOX_CSS_PATH}`,
    geocoderCdn: `${mapboxApi}/${process.env.VUE_APP_MAPBOX_GEOCODER_CDN_PATH}`,
    geocoderCss: `${mapboxApi}/${process.env.VUE_APP_MAPBOX_GEOCODER_CSS_PATH}`
};

const DEFAULT_ZOOM = 2;
const DEFAULT_USER = 'mapbox';

export const loadScript = (url) => {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = url;
        document.head.appendChild(script);
        script.onload = () => {
            resolve();
        };
    });
};

export const loadStyle = (url) => {
    return new Promise((resolve) => {
        const styles = document.createElement('link');
        styles.rel = 'stylesheet';
        styles.href = url;
        document.head.appendChild(styles);
        styles.onload = () => {
            resolve();
        };
    });
};

export const addMapDependencies = () => {
    // check if mapbox scripts are already loaded
    const scripts = document.getElementsByTagName('script');
    for (const script of scripts) {
        const { src } = script;
        if (src.includes(resources.mapboxCdn) || src.includes(resources.geocoderCdn)) return;
    }

    const dependencies = [
        // Mapbox dependencies
        loadScript(resources.mapboxCdn),
        loadStyle(resources.mapboxCss),
        // Geocoder dependencies
        loadScript(resources.geocoderCdn),
        loadStyle(resources.geocoderCss)
    ];

    return Promise.all(dependencies);
};

export const createModal = (elementId = 'map-modal') => {
    const exits = document.getElementById(elementId);
    if (exits) return exits;

    const modal = document.createElement('div');
    modal.id = elementId;
    modal.classList.add('map-selector-container');

    const backdrop = document.createElement('div');
    backdrop.id = 'modal-backdrop';
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    return modal;
};

export const closeModal = (modal) => {
    modal.parentElement.style.display = 'none';
    modal.removeAttribute('open');
};

export const openModal = (modal) => {
    modal.parentElement.style.display = 'block';
    modal.setAttribute('open', '');
};

export const createMap = (container) => {
    const mapContainer = document.createElement('div');
    mapContainer.id = 'map';
    container.appendChild(mapContainer);

    mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
        container: 'map',
        zoom: DEFAULT_ZOOM,
        preserveDrawingBuffer: true,
        trackResize: false,
        dragRotate: false
    });
    map.addControl(new mapboxgl.NavigationControl());

    // remove defaults mapbox help links
    const mapActionContianer = mapContainer.getElementsByClassName('mapboxgl-control-container')[0];
    mapActionContianer.removeChild(mapActionContianer.children[3]);
    mapActionContianer.removeChild(mapActionContianer.children[2]);

    return map;
};

export const createGeocoder = (map) => {
    const geocoderContainer = document.createElement('div');
    geocoderContainer.id = 'geocoder';

    const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        placeholder: 'Search Location'
    });
    geocoderContainer.appendChild(geocoder.onAdd(map));
    return { geocoder, geocoderContainer };
};

export const clearGeocoder = (geocoder) => {
    geocoder.clear();
    document.getElementsByClassName('mapboxgl-ctrl-geocoder--input')[0].blur();
};

export const createDraggableMarker = (map) => {
    const { lng, lat } = map.getCenter();
    const marker = new mapboxgl.Marker({
        draggable: true,
        color: 'red'
    })
        .setLngLat([lng, lat])
        .addTo(map);

    return marker;
};

export const createMapWrapper = (container) => {
    const wrapperExits = document.getElementsByClassName('map-wrapper');
    if (wrapperExits.length) {
        while (container.firstChild) {
            container.removeChild(container.firstChild);
        }
    }

    const mapWrapper = document.createElement('div');
    mapWrapper.classList.add('map-wrapper');
    container.appendChild(mapWrapper);

    const map = createMap(mapWrapper);
    const { geocoder, geocoderContainer } = createGeocoder(map);
    mapWrapper.insertBefore(geocoderContainer, map.getContainer());
    return { map, geocoder };
};

export const createModalActions = (container, nextCallback, cancelCallback) => {
    const actionsContainer = document.createElement('div');
    actionsContainer.classList.add('actions-container');
    // back btn
    const cancelBtn = document.createElement('button');
    cancelBtn.classList.add('custom-btn', 'back-btn');
    cancelBtn.id = 'cancel-btn';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.onclick = cancelCallback ? cancelCallback : () => closeModal(container);
    // next btn
    const nextBtn = document.createElement('button');
    nextBtn.classList.add('custom-btn', 'next-btn');
    nextBtn.id = 'continue-btn';
    nextBtn.textContent = 'Select';
    nextBtn.onclick = nextCallback;

    // insert buttons on the DOM
    actionsContainer.append(cancelBtn, nextBtn);
    container.appendChild(actionsContainer);
};

export const setActionsCallback = (callback, cancelCallback) => {
    const continueBtn = document.getElementById('continue-btn');
    const cancelBtn = document.getElementById('cancel-btn');
    if (continueBtn) continueBtn.onclick = callback;
    if (cancelBtn && cancelCallback) cancelBtn.onclick = cancelCallback;
};

export const getCurrentMapStyle = (map) => {
    const style = map.getStyle();
    if (!style) return null;

    const sprite = style.sprite.split('/');
    return `${sprite[3]}/${sprite[4]}`;
};

const checkUserStyle = (style) => {
    if (!style) return;
    // check if the map style name has the owner user attached, otherwise add the mapbox default one
    return style.indexOf('/') == -1 ? `${DEFAULT_USER}/${style}` : style;
};

export const setMapOptions = (map, options, diffStyleChanges = true) => {
    return new Promise((resolve) => {
        const { lng, lat } = map.getCenter();
        const style = getCurrentMapStyle(map);
        const defaultStyle = checkUserStyle(options.defaultStyle);

        if (defaultStyle && defaultStyle !== style) {
            /* in some cases diffStyleChanges will be on false to force a full update of the style,
            remove the current style and building the given one instead of attempting a 
            diff-based update */
            map.setStyle(`mapbox://styles/${defaultStyle}`, { diff: diffStyleChanges });
        }

        if (options.latitude && options.longitude && (options.latitude != lat || options.longitude != lng)) {
            map.setCenter([Number(options.longitude), Number(options.latitude)]);
        }

        map.setZoom(options.zoom || DEFAULT_ZOOM);

        /* use idle mapbox event to wait until all mapbox request are 
        done and the style is fully loaded, then return the map snapshot */
        map.once('idle', () => {
            /* Use a recursive function to check the style load state, due a mapbox issue with the styles load, 
            in some cases even if idle event is triggered the styles are not already loaded */
            const waitStyleLoad = () => {
                const mapStyle = getCurrentMapStyle(map);
                if (mapStyle == defaultStyle) {
                    // check if the map and its styles are fully loaded
                    if (map.loaded() && map.isStyleLoaded() && map.areTilesLoaded()) {
                        return resolve();
                    }
                }
                setTimeout(waitStyleLoad, 200);
            };
            waitStyleLoad();
        });
    });
};

export const getMapSnapshot = (map) => map.getCanvas().toDataURL();

const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};

export const getStaticMapImage = async (options) => {
    try {
        const response = await fetch(
            `${mapboxApi}/styles/v1/${checkUserStyle(options.defaultStyle)}/static/${options.longitude},${
                options.latitude
            },${options.zoom},0,0/500x500?logo=false&attribution=false&access_token=${mapboxgl.accessToken}`
        );

        const buffer = await response.arrayBuffer();
        return `data:image/png;base64,${arrayBufferToBase64(buffer)}`;
    } catch (error) {
        window.rumReportError(error)
    }
};

// Converts decimal degrees to DMS (Degrees, Minutes, Seconds)
export const toDMS = (deg, isLat) => {
    try {
        const num = Number(deg);
        if (isNaN(num)) return '';

        const absolute = Math.abs(num);
        const degrees = Math.floor(absolute);
        const minutesNotTruncated = (absolute - degrees) * 60;
        const minutes = Math.floor(minutesNotTruncated);
        let seconds = parseFloat(((minutesNotTruncated - minutes) * 60).toFixed(3));

        // Fix rounding edge case: seconds reaching 60
        let finalMinutes = minutes;
        let finalDegrees = degrees;

        if (seconds >= 60) {
            seconds = 0;
            finalMinutes += 1;
        }
        if (finalMinutes >= 60) {
            finalMinutes = 0;
            finalDegrees += 1;
        }

        const direction = isLat ? (num >= 0 ? 'N' : 'S') : (num >= 0 ? 'E' : 'W');

        return `${finalDegrees}° ${finalMinutes}' ${seconds.toFixed(3)}" ${direction}`;
    } catch (error) {
        console.error('Error converting to DMS', error);
        return '';
    }
};

export const getMapCoordinates = (latitude, longitude) => {
    if (latitude == null || longitude == null) return { dd: '', dms: '' };
    try {
        const dd = `${latitude}, ${longitude}`;
        const dms = `${toDMS(latitude, true)}, ${toDMS(longitude, false)}`;
        return { dd, dms };
    } catch (error) {
        console.error('Error getting map coordinates', error);
        return { dd: '', dms: '' };
    }
};
